import { useEffect, useRef } from 'react'
import { Select } from '@/components/ui/select'
import { formatMonth } from '@/lib/formatDate'
import { addMonths, monthOf, monthsBetween } from '@/lib/spendMonth'
import { Field } from './fields'

/**
 * "For spending in": the month an income is meant to cover — the date's month or the next (salary
 * paid on 30 Sep for October). Moving the date keeps the choice's offset, so 30 Sep → October
 * becomes 1 Oct → November. `byUser` is true only for a pick, so the form can stop pre-filling.
 */
export function SpendMonthField({
  date,
  value,
  onChange,
}: {
  date: string
  value: string
  onChange: (month: string, byUser: boolean) => void
}) {
  const own = date ? monthOf(date) : value
  const previousDate = useRef(date)

  useEffect(() => {
    const before = previousDate.current
    previousDate.current = date
    if (!before || !date || before === date) return
    const offset = Math.max(0, Math.min(1, monthsBetween(monthOf(before), value)))
    const next = addMonths(monthOf(date), offset)
    if (next !== value) onChange(next, false)
  }, [date, value, onChange])

  return (
    <Field label="For spending in">
      <Select aria-label="For spending in" value={value} onChange={(v) => onChange(v, true)}>
        <option value={own}>{formatMonth(own)}</option>
        <option value={addMonths(own, 1)}>{formatMonth(addMonths(own, 1))}</option>
      </Select>
      <p className="mt-1 text-xs text-[var(--fl-text-2)]">The month this money is meant to cover</p>
    </Field>
  )
}
